import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { userAPI } from '../services/api';
import { useI18n } from '../i18n/i18nContext';
import { useLocalizedNavigate } from '../hooks/useLocalizedNavigate';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { Package } from '@shared/types';
import { useUser } from '../contexts/UserContext';
import ContentPageRenderer, { ContentPageData } from '../components/ContentPageRenderer';
import { axiosClient } from '../services/api';
import { pushToDataLayer, clearEcommerce, formatPackageItem } from '../utils/dataLayer';
import MembershipTable from '../components/MembershipTable';
import PackagesTable from '../components/PackagesTable';
import { usePackagesStore } from '../stores/packagesStore';
import SEO from '../components/SEO';

const PricingPage: React.FC = () => {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const { t, language } = useI18n();
  useDocumentTitle(t.pageTitles.pricing);
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const { user } = useUser();

  const { packages: storePackages, promotionDetails, loading: storeLoading, fetchPackages } = usePackagesStore();
  
  const [filteredPackages, setFilteredPackages] = useState<Package[]>(storePackages);
  const [contentPages, setContentPages] = useState<Record<string, ContentPageData | null>>({});
  const [contentLoading, setContentLoading] = useState(true);
  const [showSpousePricing, setShowSpousePricing] = useState(false);
  const [spouseFiltering, setSpouseFiltering] = useState(false);
  const hasContentFetched = useRef(false);
  const hasFetchedPackages = useRef(false);

  useEffect(() => {
    if (!hasFetchedPackages.current) {
      hasFetchedPackages.current = true;
      fetchPackages();
    }
  }, [fetchPackages]);

  useEffect(() => {
    const applySpouseFiltering = async () => {
      const metaData = user?.metaData || {};
      const isLinked = metaData.spouse_status === 'linked';
      const isInviteeSpouse = isLinked && metaData.spouse_role === 'invitee';

      if (!isInviteeSpouse || !metaData.spouse_id || storePackages.length === 0) {
        setFilteredPackages(storePackages);
        return;
      }

      setSpouseFiltering(true);
      try {
        const spousePriceResponse = await userAPI.getSpouseMaxPrice();
        const { maxSpousePrice } = spousePriceResponse.data;

        if (maxSpousePrice !== null && maxSpousePrice !== undefined) {
          const filtered = storePackages.filter((pkg: Package) => {
            if (pkg.isSpousePackage) {
              return pkg.originalPrice != null && pkg.originalPrice <= maxSpousePrice;
            } else {
              return pkg.price > maxSpousePrice;
            }
          });
          setFilteredPackages(filtered);
        } else {
          setFilteredPackages(storePackages);
        }
      } catch (error) {
        console.error('[Spouse Invitee] Error fetching spouse max price:', error);
        setFilteredPackages(storePackages);
      } finally {
        setSpouseFiltering(false);
      }
    };

    applySpouseFiltering();
  }, [storePackages, user?.metaData]);

  useEffect(() => {
    if (hasContentFetched.current) return;
    hasContentFetched.current = true;

    const fetchContentPages = async () => {
      try {
        const slugs = ['pricing-intro', 'pricing-subscriptions-top', 'pricing-membership-info', 'pricing-packages-info'];
        const response = await axiosClient.get<Record<string, ContentPageData>>(`/api/content-pages/batch/${slugs.join(',')}`);
        setContentPages(response.data);
      } catch (err) {
        console.error('Failed to fetch content pages:', err);
      } finally {
        setContentLoading(false);
      }
    };

    fetchContentPages();
  }, []);

  useEffect(() => {
    if (location.hash) {
      const elementId = location.hash.substring(1);
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  const hasTrackedViewItemList = useRef(false);
  useEffect(() => {
    if (filteredPackages.length > 0 && !storeLoading && !hasTrackedViewItemList.current) {
      hasTrackedViewItemList.current = true;
      clearEcommerce();
      pushToDataLayer('view_item_list', {
        content: { category: 'subscription' }
      });
    }
  }, [filteredPackages, storeLoading]);

  const handlePurchase = (packageId: string) => {
    const pkg = filteredPackages.find(p => p.id === packageId);
    
    if (pkg) {
      clearEcommerce();
      pushToDataLayer('add_to_cart', {
        content: { category: pkg.type === 'subscription' ? 'subscription' : 'package' },
        ecommerce: {
          items: [formatPackageItem(pkg, pkg.type === 'subscription' ? 'Subscription' : 'Package')]
        }
      });
    }

    if (!isAuthenticated) {
      loginWithRedirect({
        appState: { 
          returnTo: `/checkout/${packageId}`
        }
      });
    } else {
      navigate(`/checkout/${packageId}`);
    }
  };

  const loading = storeLoading || spouseFiltering;

  if (loading && filteredPackages.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">{t.packages.loadingPackages}</div>
      </div>
    );
  }

  const metaData = user?.metaData || {};
  const isLinked = metaData.spouse_status === 'linked';
  const isInviteeSpouse = isLinked && metaData.spouse_role === 'invitee';

  const displayedPackages = (filteredPackages || []).filter(pkg => {
    if (pkg.price <= 0) {
      return false;
    }

    const now = new Date();
    const hasPromotionalPricing = pkg.pricingRules && pkg.pricingRules.length > 0 && 
      pkg.pricingRules.some(rule => {
        const startValid = !rule.startDate || new Date(rule.startDate) <= now;
        const endValid = !rule.endDate || new Date(rule.endDate) >= now;
        return startValid && endValid && rule.priority >= 100;
      });

    if (hasPromotionalPricing) {
      return false;
    }

    if (isInviteeSpouse) {
      return true;
    } else {
      return pkg.isSpousePackage === showSpousePricing;
    }
  });
  
  const subscriptions = displayedPackages
    .filter(pkg => pkg.type === 'subscription')
    .sort((a, b) => a.price - b.price);

  const bulkPackages = displayedPackages
    .filter(pkg => pkg.type === 'bulk')
    .sort((a, b) => a.price - b.price);

  return (
    <div className="py-16 bg-gray-50">
      <SEO description={t.seo.pricingDescription} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t.pricing.title}
          </h1>
          <p className="text-lg text-gray-600">
            {t.pricing.subtitle}
          </p>
        </div>

        {!contentLoading && (
          <ContentPageRenderer 
            slug="pricing-intro" 
            className="max-w-5xl mb-8"
            preloadedContent={contentPages['pricing-intro']}
          />
        )}
        

        {promotionDetails && promotionDetails.id !== 'regular' && (
          <div className="max-w-3xl mx-auto mb-12 p-6 bg-green-50 border-2 border-green-300 rounded-lg">
            <div className="text-center">
              <div 
                className="text-gray-700" 
                dangerouslySetInnerHTML={{ 
                  __html: promotionDetails.content[language as keyof typeof promotionDetails.content] || promotionDetails.content.en 
                }}
              />
            </div>
          </div>
        )}

        <div className="max-w-5xl mx-auto">
          {subscriptions.length > 0 && (
            <div>
              {!contentLoading && (
                <ContentPageRenderer 
                  slug="pricing-subscriptions-top" 
                  className="mb-6"
                  preloadedContent={contentPages['pricing-subscriptions-top']}
                />
              )}
              {/* Temporarily hidden per client request - Do not remove logic */}
              <div className="hidden">
                {user?.metaData?.spouse_status !== 'linked' && (
                  <div className="flex items-center">
                    <span className="text-gray-700 mr-4">{t.packages.showSpousePricing}</span>
                    <label htmlFor="spouse-toggle" className="flex items-center cursor-pointer">
                      <div className="relative">
                        <input
                          id="spouse-toggle"
                          type="checkbox"
                          className="sr-only"
                          checked={showSpousePricing}
                          onChange={() => setShowSpousePricing(!showSpousePricing)}
                        />
                        <div className={`block w-14 h-8 rounded-full transition-colors ${
                          showSpousePricing ? 'bg-blue-600' : 'bg-gray-300'
                        }`}></div>
                        <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${
                          showSpousePricing ? 'transform translate-x-6' : ''
                        }`}></div>
                      </div>
                    </label>
                  </div>
                )}
              </div>
              <MembershipTable
                items={subscriptions}
                language={language}
                onSubscribe={(pkg) => handlePurchase(pkg.id)}
                subscribeLabel={t.packages.subscribe}
                perSessionLabel={t.packages.perSession}
                promotionTitle={promotionDetails?.title?.[language as keyof typeof promotionDetails.title] || promotionDetails?.title?.en}
              />
              
              {!contentLoading && (
                <ContentPageRenderer 
                  slug="pricing-membership-info" 
                  className="mt-8"
                  preloadedContent={contentPages['pricing-membership-info']}
                />
              )}
            </div>
          )}

          <div id="packages">
            {bulkPackages.length > 0 && (
              <div>
                <PackagesTable
                  items={bulkPackages}
                  language={language}
                  onPurchase={(pkg) => handlePurchase(pkg.id)}
                  purchaseLabel={t.packages.purchase}
                  perSessionLabel={t.packages.perSession}
                  promotionTitle={
                    promotionDetails?.package_title?.[language as keyof typeof promotionDetails.package_title] || 
                    promotionDetails?.title?.[language as keyof typeof promotionDetails.title] || 
                    promotionDetails?.title?.en
                  }
                />

                {!contentLoading && (
                  <ContentPageRenderer 
                    slug="pricing-packages-info" 
                    preloadedContent={contentPages['pricing-packages-info']}
                  />
                )}
              </div>
            )}
          </div>
        
          {showSpousePricing && (
            <div className="text-center mt-8">
              <p className="text-sm text-gray-500">{t.packages.spouseDisclaimer}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
